import { useState } from 'react';
import { Loader2, ShieldCheck, AlertCircle, Send, KeyRound, ExternalLink, MessageCircle } from 'lucide-react';
import { api } from '../lib/api';
import { classifyUserAgent, openInChromeUrl, isIOS } from '../lib/inAppBrowser';

/**
 * Iter-52.55.8 / .10 — Manual OTP fallback for the MSG91 widget.
 *
 * Shown when Msg91Widget can't load the SDK (typical in
 * WhatsApp/Facebook/Instagram in-app browsers). Two steps —
 *
 *   1. `phone`  — user types 10-digit Indian mobile → POST
 *                 /auth/msg91/otp/send → server calls MSG91
 *                 widget/sendOtp (same widget config as SDK → the
 *                 OTP arrives on WhatsApp on our account).
 *   2. `otp`    — user types the code → POST /auth/msg91/otp/verify
 *                 → server calls widget/verifyOtp → returns a real
 *                 MSG91 access-token the parent forwards to
 *                 /auth/register-with-otp or /auth/login-with-mobile-otp.
 *
 * Iter-52.55.10 — when send fails with 502 ("OTP service didn't
 * respond / not configured / couldn't send right now"), we swap the
 * inline UI for the ESCAPE-HATCH panel: bilingual copy, one-tap
 * "Open in Chrome" button, and a WhatsApp button pre-filled with a
 * support message. We also fire a `fallback_unavailable` funnel
 * event so the admin can Nudge these leads manually.
 */

const WHATSAPP_SUPPORT_NUMBER = '919599668449';
const WHATSAPP_SUPPORT_MSG = 'Namaste, mujhe ReadBuddy mein register karne mein dikkat aa rahi hai.';

function whatsappSupportUrl() {
  return `https://wa.me/${WHATSAPP_SUPPORT_NUMBER}?text=${encodeURIComponent(WHATSAPP_SUPPORT_MSG)}`;
}

export default function Msg91ManualOtp({ onVerified, onError, sessionId, label = 'Send OTP' }) {
  const [step, setStep] = useState('phone'); // 'phone' | 'otp' | 'escape'
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  // Iter-52.55.10 — swap the whole inline UI for the escape hatch and
  // log a funnel event so the admin can follow up on WhatsApp.
  const goEscape = (reason) => {
    setStep('escape');
    try {
      api.post('/analytics/signup-event', {
        session_id: sessionId || 'msg91-manual-anon',
        event_type: 'fallback_unavailable',
        mode: 'phone_only',
        status: 'failed',
        error: (reason || 'fallback provider unavailable').toString().slice(0, 280),
        mobile_e164: mobile ? `+91${mobile}` : null,
        user_agent_class: classifyUserAgent(),
      }).catch(() => {});
    } catch { /* noop */ }
  };

  const doSend = async () => {
    setError('');
    setMessage('');
    const digits = mobile.replace(/\D/g, '').replace(/^91/, '');
    if (digits.length !== 10 || !/^[6-9]/.test(digits)) {
      setError('Please enter a valid 10-digit Indian mobile number.');
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post('/auth/msg91/otp/send', {
        mobile: digits, session_id: sessionId,
      });
      setMobile(digits);
      setStep('otp');
      setMessage(`OTP bhej diya! Aapke WhatsApp ya SMS mein ${data.expires_in_minutes || 10} min mein aa jayega.`);
      setResendCooldown(60);
      const iv = setInterval(() => {
        setResendCooldown(v => (v <= 1 ? (clearInterval(iv), 0) : v - 1));
      }, 1000);
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.detail || 'Could not send OTP.';
      // Iter-52.55.10 — 502 / 503 / network = provider unavailable →
      // escape hatch. 4xx from us (429 cooldown, 400 bad-mobile) is
      // user-recoverable → show inline error and let them retry.
      if (status === 502 || status === 503 || !status) {
        goEscape(msg);
      } else {
        setError(msg);
        onError && onError(msg);
      }
    } finally {
      setSending(false);
    }
  };

  const doVerify = async () => {
    setError('');
    if (!/^\d{4,8}$/.test(otp.trim())) {
      setError('Please enter the code you received.');
      return;
    }
    setVerifying(true);
    try {
      const { data } = await api.post('/auth/msg91/otp/verify', {
        mobile, otp: otp.trim(), session_id: sessionId,
      });
      onVerified && onVerified(data.access_token, `+91${mobile}`);
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.detail || 'That code doesn\'t match. Please try again.';
      if (status === 502 || status === 503 || !status) {
        goEscape(msg);
      } else {
        setError(msg);
        onError && onError(msg);
      }
    } finally {
      setVerifying(false);
    }
  };

  // Iter-52.55.10 — escape-hatch panel. Shown when the OTP provider
  // is unreachable so a real customer never sees a 502/503 or a raw
  // error message.
  if (step === 'escape') {
    return (
      <div data-testid="msg91-manual-escape"
        className="rounded-2xl border-2 border-amber-400 bg-amber-50 p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm font-black text-amber-900">
          <AlertCircle size={18}/> Verification is having trouble right now.
        </div>
        <p className="text-[12px] md:text-sm font-bold text-amber-900 leading-snug">
          Kripya ye link Chrome mein kholiye, ya humse WhatsApp par baat kijiye.
          <br/>कृपया यह लिंक Chrome में खोलें, या हमसे WhatsApp पर बात करें।
        </p>
        <div className="flex flex-col md:flex-row gap-2">
          {isIOS() ? (
            <div className="flex-1 text-[11px] font-bold text-amber-900 bg-white/70 rounded-lg px-3 py-2 border border-amber-300">
              Tap the "•••" or share icon at the bottom and choose <b>Open in Safari</b>.
            </div>
          ) : (
            <a href={openInChromeUrl()}
              data-testid="msg91-escape-open-chrome"
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-amber-600 hover:bg-amber-700 text-white font-black px-3 py-2 text-sm">
              <ExternalLink size={14}/> Open in Chrome
            </a>
          )}
          <a href={whatsappSupportUrl()}
            data-testid="msg91-escape-whatsapp"
            target="_blank" rel="noreferrer"
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-black px-3 py-2 text-sm">
            <MessageCircle size={14}/> WhatsApp us
          </a>
        </div>
        <p className="text-[10px] text-amber-800">
          Support: +91 95996 68449
        </p>
      </div>
    );
  }

  return (
    <div data-testid="msg91-manual-otp" className="rounded-2xl border-2 border-[#FF6B1A]/50 bg-white p-4 space-y-3">
      <div className="flex items-center gap-2 text-sm font-black text-[#FF6B1A]">
        <ShieldCheck size={18}/>
        Manual OTP verification / मैनुअल OTP
      </div>
      <p data-testid="manual-otp-channel-hint" className="text-[11px] font-bold text-slate-700 leading-snug bg-slate-50 border border-slate-200 rounded px-2 py-1">
        📱 OTP aapke WhatsApp par aayega. / OTP आपके WhatsApp पर आएगा।
      </p>
      {step === 'phone' && (
        <div className="space-y-2">
          <label className="block text-xs font-bold text-[#2B2140]">
            Mobile number / मोबाइल नंबर
          </label>
          <div className="flex items-center gap-2">
            <span className="text-sm font-black text-slate-500 shrink-0">+91</span>
            <input
              data-testid="manual-otp-mobile"
              type="tel"
              inputMode="numeric"
              maxLength={10}
              value={mobile}
              onChange={e => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
              placeholder="98765 43210"
              className="flex-1 px-3 py-2 rounded-lg border-2 border-slate-300 text-base font-bold focus:outline-none focus:border-[#FF6B1A]"
            />
          </div>
          <button
            data-testid="manual-otp-send"
            type="button"
            onClick={doSend}
            disabled={sending || mobile.length !== 10}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#FF6B1A] hover:bg-[#E55A0F] text-white font-black py-2.5 disabled:opacity-50"
          >
            {sending ? <Loader2 size={16} className="animate-spin"/> : <Send size={16}/>}
            {sending ? 'Sending OTP…' : label}
          </button>
        </div>
      )}
      {step === 'otp' && (
        <div className="space-y-2">
          {message && (
            <p data-testid="manual-otp-sent-msg" className="text-xs font-bold text-emerald-700 leading-snug">
              {message}
            </p>
          )}
          <label className="block text-xs font-bold text-[#2B2140]">
            OTP code
          </label>
          <div className="flex items-center gap-2">
            <KeyRound size={18} className="text-slate-500 shrink-0"/>
            <input
              data-testid="manual-otp-code"
              type="tel"
              inputMode="numeric"
              maxLength={8}
              value={otp}
              onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 8))}
              placeholder="123456"
              autoFocus
              className="flex-1 px-3 py-2 rounded-lg border-2 border-slate-300 text-lg font-black tracking-widest text-center focus:outline-none focus:border-[#FF6B1A]"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="manual-otp-verify"
              type="button"
              onClick={doVerify}
              disabled={verifying || otp.length < 4}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#FF6B1A] hover:bg-[#E55A0F] text-white font-black py-2.5 disabled:opacity-50"
            >
              {verifying ? <Loader2 size={16} className="animate-spin"/> : <ShieldCheck size={16}/>}
              {verifying ? 'Verifying…' : 'Verify OTP'}
            </button>
            <button
              data-testid="manual-otp-resend"
              type="button"
              onClick={doSend}
              disabled={sending || resendCooldown > 0}
              className="text-[11px] font-black text-slate-500 hover:text-[#FF6B1A] disabled:opacity-40 px-3 py-2"
            >
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend'}
            </button>
          </div>
        </div>
      )}
      {error && (
        <div data-testid="manual-otp-error" className="flex items-start gap-2 text-xs font-bold text-rose-600">
          <AlertCircle size={14} className="mt-0.5 shrink-0"/>
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}
